import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PanelLoading } from "../../../components/Loading";
import { KPI } from "../../../components/primitives.jsx";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { ArticleCombobox } from "../../../components/ArticleCombobox";
import { useChartPalette } from "../../../theme/useChartPalette";
import { localizedName } from "../../../lib/localizedName";
import { formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import { labArchiveApi, type ArchiveArticleOption, type ParameterMovement } from "../../../api/labArchive";
import type { ControlPoint } from "../../../api/controlPoints";

/** The chart's own height. One number, because the wait has to reserve exactly what the chart will take. */
export const VISINA_GRAFIKONA = 280;

/**
 * What a wait must hold open when the four numbers stand above the chart.
 *
 * A card that grows when its data lands pushes everything under it down, and the reader loses the row
 * they were looking at. So the wait reserves the whole shape: the numbers, the chart and the sentence
 * that explains it.
 */
export const VISINA_SA_BROJEVIMA = VISINA_GRAFIKONA + 190;

/**
 * One parameter's movement, drawn — no filters of its own, nothing read from the server.
 *
 * The chart above the analyses list speaks the same visual language, so that language lives in one place:
 * a point is a DAY, the line is the daily average, the band is that day's spread from lowest to highest. A
 * parameter with five thousand measurements a year shows nothing as a run of individual points, while the
 * spread carries what the average hides.
 *
 * The four numbers above the chart come from the SAME response the line is drawn from. That is not a
 * saving but a rule: a number standing over a chart while being computed from something else claims the
 * chart is about something other than what it is.
 */
export function ParameterMovementChart({
  data,
  lang,
  range,
  emptyText,
}: {
  data: ParameterMovement;
  lang: string;
  /** Set when dates, not a year, decided the span — then the sentence may not speak of a year. */
  range?: { from: string; to: string } | null;
  emptyText: string;
}) {
  const { t } = useTranslation();
  const palette = useChartPalette();

  const n = (v: number) => v.toLocaleString(lang);
  const broj = (v: number | null) => (v === null ? "—" : String(v));

  const tacke = (data.days ?? []).map((d) => ({
    dan: d.day,
    opseg: [Number(d.minValue), Number(d.maxValue)] as [number, number],
    prosek: d.avgValue === null ? null : Number(d.avgValue),
    merenja: d.measurements,
  }));

  if (tacke.length === 0) return <div className="empty">{emptyText}</div>;

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <KPI
          label={t("lab.param.measurements", "Merenja sa brojem")}
          value={n(data.measurements)}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />
        <KPI
          label={t("lab.param.min", "Najniže")}
          value={broj(data.minValue)}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />
        <KPI
          label={t("lab.param.avg", "Prosek")}
          value={broj(data.avgValue)}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />
        <KPI
          label={t("lab.param.max", "Najviše")}
          value={broj(data.maxValue)}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />
      </div>

      <ResponsiveContainer width="100%" height={VISINA_GRAFIKONA}>
        <ComposedChart data={tacke} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="dan"
            tick={{ fontSize: 11, fill: palette.ink }}
            axisLine={false}
            tickLine={false}
            minTickGap={44}
            tickFormatter={(v) => formatDate(String(v), lang)}
          />
          <YAxis tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} width={58} />
          <Legend
            verticalAlign="top"
            align="right"
            height={24}
            wrapperStyle={{ fontSize: 12, color: "var(--ink-3)" }}
          />
          <Tooltip
            contentStyle={{
              background: palette.surface,
              border: `1px solid ${palette.border}`,
              borderRadius: 8,
            }}
            labelFormatter={(label) => formatDate(String(label), lang)}
            formatter={(value, name) =>
              Array.isArray(value)
                ? [`${value[0]} – ${value[1]} ${data.unit}`, String(name)]
                : [`${value} ${data.unit}`, String(name)]
            }
          />
          <Area
            dataKey="opseg"
            name={t("lab.movement.band", "Od najniže do najviše tog dana")}
            stroke="none"
            fill={palette.chart[0] || palette.brand}
            fillOpacity={0.16}
          />
          <Line
            type="monotone"
            dataKey="prosek"
            name={t("lab.param.avg", "Prosek")}
            stroke={palette.chart[0] || palette.brandInk}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* What the line and the band are is the legend's job. What is left here is the one thing the
          chart cannot show: a gap is a day nobody measured, not a quiet day — and that is only legible
          next to how many days carry a measurement at all. */}
      <p className="muted" style={{ margin: "12px 0 0", maxWidth: "76ch", lineHeight: 1.55 }}>
        {range
          ? t("lab.movement.daysRange", "{{dana}} dana sa merenjem između {{od}} i {{do}}; prazan razmak je dan kad se nije merilo.", {
              dana: n(tacke.length),
              od: formatDate(range.from, lang),
              do: formatDate(range.to, lang),
            })
          : t("lab.movement.days", "{{dana}} dana sa merenjem u {{godina}}; prazan razmak je dan kad se nije merilo.", {
              dana: n(tacke.length),
              godina: data.year,
            })}
      </p>
    </>
  );
}

/**
 * One parameter's movement through the chosen year, at the chosen point and for the chosen article.
 *
 * This is an operational reading, not an archaeological one: the yearly tally says which year is worth a
 * look, and this chart says what happened inside it — where the value stood, how much it swung, and on
 * which days anything was measured at all. A KPI is built from that; nothing is built from a ten-year
 * average.
 *
 * Three selectors, and every one of them moves the level the line stands at. The control point, because
 * the same parameter sits at two different levels on raw soybean and on finished meal — a line through
 * both is a line through a gap no process ever ran in. The article, for the same reason inside a single
 * point: protein on meal and protein on fermented protein are measured on one line and are not the same
 * quantity. The year, because an indicator is always asked for over a period, and the reader picks the
 * period.
 *
 * The counts in the parameter list on the left are for the WHOLE archive and stay that way — no call
 * returns a per-parameter measurement count for a single article. So the screen says so in words, right
 * where those counts stand.
 */
export function ParameterMovementCard({
  parameterId,
  points,
  pointName,
  lang,
  articles,
  articleId,
  onArticle,
}: {
  parameterId: string;
  points: ControlPoint[];
  pointName: (p: ControlPoint) => string;
  lang: string;
  articles: ArchiveArticleOption[];
  articleId: string;
  onArticle: (erpProductId: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [year, setYear] = useState<number | undefined>(undefined);
  const [pointId, setPointId] = useState("");
  const [data, setData] = useState<ParameterMovement | null>(null);

  // Parametar se menja -> godina se vraća na podrazumevanu, jer godina jednog parametra ne mora da
  // postoji kod drugog; servis bi tiho vratio najnoviju i izbor na ekranu bi lagao.
  useEffect(() => {
    setYear(undefined);
    setPointId("");
  }, [parameterId]);

  useEffect(() => {
    let otkazano = false;
    setData(null);
    void labArchiveApi
      .movement(parameterId, {
        year,
        controlPointId: pointId || undefined,
        erpProductId: articleId || undefined,
      })
      .then((r) => {
        if (!otkazano) setData(r);
      })
      .catch((e: ApiError) => toast.error(e.message));
    return () => {
      otkazano = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterId, year, pointId, articleId]);

  const prazno = articleId
    ? t(
        "lab.movement.noneArticle",
        "Za izabrani artikal, u izabranoj godini i na izabranoj tački, ovaj parametar nije izmeren.",
      )
    : t("lab.movement.none", "U izabranoj godini i na izabranoj tački nema merenja.");

  return (
    <div className="card">
      <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
        <strong>{t("lab.movement.title", "Kretanje kroz godinu")}</strong>
        {data && (
          <span className="muted">
            {localizedName({ name: data.key, nameI18n: data.nameI18n }, lang)} · {data.unit}
          </span>
        )}
      </div>
      <div className="card-b">
        <div className="toolbar">
          <Select
            value={year === undefined ? String(data?.year ?? "") : String(year)}
            options={(data?.availableYears ?? []).map((g) => ({ value: String(g), label: String(g) }))}
            onChange={(v) => setYear(Number(v))}
            ariaLabel={t("lab.movement.year", "Godina")}
            style={{ minWidth: 120 }}
          />
          <Select
            value={pointId}
            options={[
              { value: "", label: t("lab.filter.allPoints", "Sve kontrolne tačke") },
              ...points.map((p) => ({ value: p.id, label: pointName(p) })),
            ]}
            onChange={setPointId}
            ariaLabel={t("lab.filter.point", "Kontrolna tačka")}
            style={{ minWidth: 220 }}
          />
          <ArticleCombobox articles={articles} value={articleId} onChange={onArticle} />
        </div>

        <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
          {t(
            "lab.movement.scope",
            "Četiri broja i grafikon su za ova tri izbora — godinu, kontrolnu tačku i artikal. Tabele ispod imaju svoje filtere.",
          )}
        </p>

        {!data ? (
          <div
            style={{
              minHeight: VISINA_SA_BROJEVIMA,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PanelLoading text={t("common.loading")} />
          </div>
        ) : (
          <ParameterMovementChart data={data} lang={lang} emptyText={prazno} />
        )}
      </div>
    </div>
  );
}
